<template>
  <div class="content">
    <div class="col-md-8 flex-container-top">
      <h2 class="text-eveer">CMS Pages</h2>
      <base-button
        v-if="role == 'Admin'"
        type="primary"
        round
        icon
        class="top-button"
        @click="$router.push('add?id=-1')"
      >
        <i class="fa fa-plus"></i>
      </base-button>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">CMS Pages List</h4>
          <div>
            <div
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <el-select
                class="select-primary mb-3 pagination-select"
                v-model="pagination.perPage"
                placeholder="Per page"
              >
                <el-option
                  class="select-primary"
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>

              <base-input>
                <el-input
                  type="search"
                  class="mb-3 search-input"
                  clearable
                  prefix-icon="el-icon-search"
                  placeholder="Search CMS pages"
                  v-model="searchQuery"
                  aria-controls="datatables"
                >
                </el-input>
              </base-input>
            </div>

            <el-table :data="queriedData" style="font-size: 13px">
              <el-table-column :min-width="220" align="left" label="Title">
                <div slot-scope="props">
                  <div style="color: #ba54f5; font-weight: 300">
                    {{ getTitle(props.row) }}
                  </div>
                </div>
              </el-table-column>

              <el-table-column :min-width="180" align="left" label="Page">
                <div slot-scope="props">
                  <div>{{ getPageName(props.row) }}</div>
                </div>
              </el-table-column>

              <el-table-column :min-width="220" align="left" label="Preview">
                <div slot-scope="props">
                  <div class="cms-preview">{{ getPreview(props.row) }}</div>
                </div>
              </el-table-column>

              <el-table-column :min-width="120" align="left" label="Status">
                <div slot-scope="props">
                  <span class="cms-status" :class="getStatus(props.row)">
                    {{ getStatus(props.row) }}
                  </span>
                </div>
              </el-table-column>

              <el-table-column :min-width="160" align="left" label="Updated">
                <div slot-scope="props">
                  <div>{{ formatDate(getUpdatedAt(props.row)) }}</div>
                </div>
              </el-table-column>

              <el-table-column :min-width="150" align="right" label="Control">
                <div slot-scope="props">
                  <i
                    class="fa fa-eye text-info icon-padding"
                    @click="openPreview(props.row)"
                    title="Preview CMS Page"
                    style="cursor: pointer"
                  >
                  </i>
                  <i
                    v-if="role == 'Admin'"
                    :class="[
                      getId(props.row) ? 'fa fa-edit text-warning' : 'fa fa-plus text-primary',
                      'icon-padding',
                    ]"
                    @click="openCmsPage(props.row)"
                    :title="getId(props.row) ? 'Edit CMS Page' : 'Create CMS Page'"
                    style="cursor: pointer"
                  >
                  </i>
                </div>
              </el-table-column>
            </el-table>
          </div>

          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div>
              <p class="card-category">
                Showing {{ total ? from + 1 : 0 }} to {{ to }} of {{ total }}
                entries
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <div class="row loader-pos">
      <div class="sk-chase" v-if="loading">
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
      </div>

      <div class="no-data-message" v-if="noDataMessage">
        <div>There are no records found</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import { BasePagination } from "src/components";
import Fuse from "fuse.js";

const API_ENDPOINTS = {
  getBySlug: "cms-data/get-by-slug/",
};

const PAGE_TYPES = [
  { label: "About Us", page_type: "about-us", slug: "about-us" },
  {
    label: "Terms & Conditions",
    page_type: "terms-conditions",
    slug: "terms-conditions",
  },
  { label: "Privacy Policy", page_type: "privacy-policy", slug: "privacy-policy" },
  { label: "Refund Policy", page_type: "refund-policy", slug: "refund-policy" },
];

export default {
  components: {
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  computed: {
    queriedData() {
      let result = this.searchQuery ? this.searchedData : this.tableData;
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchQuery ? this.searchedData.length : this.tableData.length;
    },
  },
  data() {
    return {
      role: window.localStorage.getItem("role"),
      noDataMessage: false,
      loading: false,
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [10, 50, 100, 1000],
        total: 0,
      },
      searchQuery: "",
      tableData: [],
      searchedData: [],
      fuseSearch: null,
      pageTypes: PAGE_TYPES,
    };
  },
  async mounted() {
    await this.fetchCmsPages();
  },
  watch: {
    searchQuery(value) {
      if (!value) {
        this.searchedData = [];
        return;
      }

      const result = this.fuseSearch
        ? this.fuseSearch.search(value).map((item) => item.item)
        : [];
      this.searchedData = result;
      this.pagination.currentPage = 1;
    },
  },
  methods: {
    async fetchCmsPages() {
      this.loading = true;
      this.noDataMessage = false;

      try {
        const pages = await Promise.all(
          this.pageTypes.map((pageType) => this.fetchCmsPage(pageType))
        );

        this.tableData = pages;
        this.fuseSearch = new Fuse(this.tableData, {
          keys: ["label", "title", "page_type", "slug"],
          threshold: 0.3,
        });
        this.noDataMessage = this.tableData.length === 0;
      } finally {
        this.loading = false;
      }
    },
    async fetchCmsPage(pageType) {
      const response = await this.$apiService.getCall(
        API_ENDPOINTS.getBySlug + pageType.slug
      );

      if (response.isError) {
        return this.getEmptyPage(pageType);
      }

      const page = this.extractRecord(response.apidata);
      if (!page || this.hasApiFailure(response.apidata)) {
        return this.getEmptyPage(pageType);
      }

      return {
        ...pageType,
        ...page,
        label: pageType.label,
        page_type: page.page_type || pageType.page_type,
        slug: pageType.slug,
      };
    },
    extractRecord(apiData) {
      const candidates = [
        apiData && apiData.Data,
        apiData && apiData.data,
        apiData && apiData.cms,
        apiData && apiData.page,
        apiData && apiData.result,
      ];

      for (const item of candidates) {
        if (!item) continue;
        if (Array.isArray(item)) return item[0];
        if (item.page) return item.page;
        if (item.cms) return item.cms;
        return item;
      }

      return null;
    },
    getEmptyPage(pageType) {
      return {
        ...pageType,
        title: pageType.label,
        content: "",
        status: "not-created",
      };
    },
    openCmsPage(row) {
      const id = this.getId(row);
      const route = id ? "edit" : "add";
      const query = [
        "id=" + encodeURIComponent(id || -1),
        "slug=" + encodeURIComponent(this.getSlug(row)),
        "page_type=" + encodeURIComponent(this.getPageType(row)),
      ].join("&");

      this.$router.push(route + "?" + query);
    },
    openPreview(row) {
      const query = [
        "slug=" + encodeURIComponent(this.getSlug(row)),
        "page_type=" + encodeURIComponent(this.getPageType(row)),
      ].join("&");

      this.$router.push("preview?" + query);
    },
    getId(row) {
      return row._id || row.id;
    },
    getSlug(row) {
      return row.slug || this.getPageType(row);
    },
    getPageType(row) {
      return row.page_type || row.type || row.key || row.slug || "";
    },
    getTitle(row) {
      return row.title || row.label || "Untitled";
    },
    getPageName(row) {
      return row.label || this.getPageType(row) || "-";
    },
    getPreview(row) {
      const content = row.content || row.description || row.body || row.html || "";
      return (
        content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() ||
        "Not created yet"
      );
    },
    getStatus(row) {
      if (row.status) return row.status.toString().toLowerCase();
      if (row.active_status === false || row.is_active === false) return "inactive";
      return "active";
    },
    getUpdatedAt(row) {
      return row.updated_at || row.updatedAt || row.created_at || row.createdAt;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString();
    },
    hasApiFailure(apiData) {
      return apiData && apiData.Code && apiData.Code !== "P00001";
    },
  },
};
</script>

<style scoped>
.pagination-select,
.search-input {
  width: 200px;
}

.cms-preview {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cms-status {
  border-radius: 20px;
  display: inline-block;
  font-size: 12px;
  padding: 4px 10px;
  text-transform: capitalize;
}

.cms-status.active {
  background: rgba(0, 242, 195, 0.12);
  color: #00f2c3;
}

.cms-status.inactive {
  background: rgba(253, 93, 147, 0.12);
  color: #fd5d93;
}

.cms-status.not-created {
  background: rgba(255, 255, 255, 0.08);
  color: #adb5bd;
}

.icon-padding {
  padding: 10px;
}
</style>
